import { Static, Type } from '@sinclair/typebox';
import { ElementGroupNames } from '../../enums/ElementGroupNames';
import { createPathGuidParameter, createPathStringParameter } from '../../routes/createPathParameter';
import { okResponse } from '../../routes/defaultResponses';
import { makeNullable } from '../../utils/typeboxHelpers';

const unitVerificatieBerekeningUnityCheckSchema = Type.Object({
    score: Type.Number(),
    id: Type.String(),
    unityCheckType: Type.String(),
    unityCheckTypeAnders: Type.Optional(makeNullable(Type.String())),
    modelleerNiveau: Type.String(),
    unityCheckScore: Type.Number(),
});
const unitVerificatieBerekeningDataSchema = Type.Object({
    unityChecks: Type.Array(unitVerificatieBerekeningUnityCheckSchema),
    score: Type.Optional(makeNullable(Type.Number())),
    opmerking: Type.Optional(makeNullable(Type.String())),
});

export type UnitVerificatieBerekeningData = Static<typeof unitVerificatieBerekeningDataSchema>;
export type UnitVerificatieBerekeningUnityCheck = Static<typeof unitVerificatieBerekeningUnityCheckSchema>;

export const verificatieBerekeningPaths = {
    '/api/surveys/{surveyId}/elementGroups/{elementGroupName}/inspectionStandardData/verificatieBerekening': {
        parameters: [createPathGuidParameter('surveyId'), createPathStringParameter('elementGroupName')],
        get: {
            operationId: 'verificatieBerekeningenControllers.getElementGroup',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                projectNaam: Type.Optional(makeNullable(Type.String())),
                                status: Type.Optional(makeNullable(Type.String())),
                                berekendOpDatum: Type.Optional(makeNullable(Type.String())),
                                constructeur: Type.Optional(makeNullable(Type.String())),
                                toetsing: Type.Optional(makeNullable(Type.String())),
                                opmerking: Type.Optional(makeNullable(Type.String())),
                                toetsNiveau: Type.Optional(makeNullable(Type.String())),
                                normVersie: Type.Optional(makeNullable(Type.String())),
                                normVersieAnders: Type.Optional(makeNullable(Type.String())),
                                modelleerNiveau: Type.Optional(makeNullable(Type.String())),
                                wegindeling: Type.Optional(makeNullable(Type.String())),
                                aantalZwareVoertuigenPerJaar: Type.Optional(makeNullable(Type.String())),
                                aantalZwareVoertuigenPerJaarAnders: Type.Optional(makeNullable(Type.String())),
                                restLevensduur: Type.Optional(makeNullable(Type.String())),
                                beperkingVoertuigGewicht: Type.Optional(makeNullable(Type.String())),
                                beperkingAslast: Type.Optional(makeNullable(Type.String())),
                                bestanden: Type.Optional(
                                    Type.Array(
                                        Type.Object({
                                            assetId: Type.String(),
                                            description: Type.Optional(Type.String()),
                                            filename: Type.String(),
                                        }),
                                    ),
                                ),
                                score: Type.Optional(Type.Number()),
                            }),
                        },
                    },
                },
            },
        },
        post: {
            operationId: 'verificatieBerekeningenControllers.updateElementGroup',
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            projectNaam: Type.Optional(makeNullable(Type.String())),
                            status: Type.Optional(makeNullable(Type.String())),
                            berekendOpDatum: Type.Optional(makeNullable(Type.String())),
                            constructeur: Type.Optional(makeNullable(Type.String())),
                            toetsing: Type.Optional(makeNullable(Type.String())),
                            opmerking: Type.Optional(makeNullable(Type.String())),
                            toetsNiveau: Type.Optional(makeNullable(Type.String())),
                            normVersie: Type.Optional(makeNullable(Type.String())),
                            normVersieAnders: Type.Optional(makeNullable(Type.String())),
                            modelleerNiveau: Type.Optional(makeNullable(Type.String())),
                            wegindeling: Type.Optional(makeNullable(Type.String())),
                            aantalZwareVoertuigenPerJaar: Type.Optional(makeNullable(Type.String())),
                            aantalZwareVoertuigenPerJaarAnders: Type.Optional(makeNullable(Type.String())),
                            restLevensduur: Type.Optional(makeNullable(Type.String())),
                            beperkingVoertuigGewicht: Type.Optional(makeNullable(Type.String())),
                            beperkingAslast: Type.Optional(makeNullable(Type.String())),
                            bestanden: Type.Optional(
                                Type.Array(
                                    Type.Object({
                                        assetId: Type.String(),
                                        description: Type.Optional(Type.String()),
                                        filename: Type.String(),
                                    }),
                                ),
                            ),
                            score: Type.Optional(Type.Number()),
                        }),
                    },
                },
            },
            responses: { ...okResponse },
        },
    },
    '/api/surveys/{surveyId}/units/{unitId}/inspectionStandardData/verificatieBerekening': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId')],
        get: {
            operationId: 'verificatieBerekeningenControllers.getUnit',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: unitVerificatieBerekeningDataSchema,
                        },
                    },
                },
            },
        },
        post: {
            operationId: 'verificatieBerekeningenControllers.updateUnit',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: unitVerificatieBerekeningDataSchema,
                        },
                    },
                },
            },
            requestBody: {
                content: {
                    'application/json': {
                        schema: Type.Object({
                            opmerking: Type.Optional(makeNullable(Type.String())),
                            unityChecks: Type.Array(
                                Type.Object({
                                    id: Type.String(),
                                    unityCheckType: Type.String(),
                                    unityCheckTypeAnders: Type.Optional(makeNullable(Type.String())),
                                    modelleerNiveau: Type.String(),
                                    unityCheckScore: Type.Number(),
                                }),
                            ),
                        }),
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/units/{unitId}/elementGroups/inspectionStandardData/verificatieBerekening': {
        parameters: [createPathGuidParameter('surveyId'), createPathGuidParameter('unitId')],
        get: {
            operationId: 'verificatieBerekeningenControllers.getElementGroupByUnit',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Object({
                                projectNaam: Type.Optional(makeNullable(Type.String())),
                                status: Type.Optional(makeNullable(Type.String())),
                                berekendOpDatum: Type.Optional(makeNullable(Type.String())),
                                constructeur: Type.Optional(makeNullable(Type.String())),
                                toetsing: Type.Optional(makeNullable(Type.String())),
                                opmerking: Type.Optional(makeNullable(Type.String())),
                                toetsNiveau: Type.Optional(makeNullable(Type.String())),
                                normVersie: Type.Optional(makeNullable(Type.String())),
                                normVersieAnders: Type.Optional(makeNullable(Type.String())),
                                modelleerNiveau: Type.Optional(makeNullable(Type.String())),
                                wegindeling: Type.Optional(makeNullable(Type.String())),
                                aantalZwareVoertuigenPerJaar: Type.Optional(makeNullable(Type.String())),
                                aantalZwareVoertuigenPerJaarAnders: Type.Optional(makeNullable(Type.String())),
                                restLevensduur: Type.Optional(makeNullable(Type.String())),
                                beperkingVoertuigGewicht: Type.Optional(makeNullable(Type.String())),
                                beperkingAslast: Type.Optional(makeNullable(Type.String())),
                                bestanden: Type.Optional(
                                    Type.Array(
                                        Type.Object({
                                            assetId: Type.String(),
                                            description: Type.Optional(Type.String()),
                                            filename: Type.String(),
                                        }),
                                    ),
                                ),
                                score: Type.Optional(Type.Number()),
                            }),
                        },
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/elements/verificatieBerekening': {
        parameters: [createPathGuidParameter('surveyId')],
        get: {
            operationId: 'verificatieBerekeningenControllers.getReportData',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Array(
                                Type.Object({
                                    elementGroupName: Type.Enum(ElementGroupNames),
                                    elements: Type.Array(
                                        Type.Object({
                                            units: Type.Array(
                                                Type.Object({
                                                    inspectionStandardData: Type.Optional(
                                                        Type.Object({
                                                            unityChecks: Type.Array(
                                                                Type.Object({
                                                                    score: Type.Number(),
                                                                    id: Type.String(),
                                                                    unityCheckType: Type.String(),
                                                                    unityCheckTypeAnders: Type.Optional(makeNullable(Type.String())),
                                                                    modelleerNiveau: Type.String(),
                                                                    unityCheckScore: Type.Number(),
                                                                }),
                                                            ),
                                                            score: Type.Optional(makeNullable(Type.Number())),
                                                            opmerking: Type.Optional(makeNullable(Type.String())),
                                                        }),
                                                    ),
                                                    manifestations: Type.Array(
                                                        Type.Intersect([
                                                            Type.Object({
                                                                id: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                inspectionStandardData: Type.Object({}),
                                                            }),
                                                            Type.Object({
                                                                name: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                code: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                elementId: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                unitId: Type.String(),
                                                            }),
                                                            Type.Object({}),
                                                        ]),
                                                    ),
                                                    id: Type.String(),
                                                    name: Type.String(),
                                                    code: Type.String(),
                                                    isArchived: Type.Boolean(),
                                                    elementId: Type.String(),
                                                    material: Type.String(),
                                                }),
                                            ),
                                            score: Type.Optional(Type.Number()),
                                            id: Type.String(),
                                            inspectionStandardData: Type.Object({}),
                                            name: Type.String(),
                                            code: Type.String(),
                                            elementGroupName: Type.String(),
                                            isArchived: Type.Boolean(),
                                        }),
                                    ),
                                    inspectionStandardData: Type.Optional(
                                        Type.Object({
                                            projectNaam: Type.Optional(makeNullable(Type.String())),
                                            status: Type.Optional(makeNullable(Type.String())),
                                            berekendOpDatum: Type.Optional(makeNullable(Type.String())),
                                            constructeur: Type.Optional(makeNullable(Type.String())),
                                            toetsing: Type.Optional(makeNullable(Type.String())),
                                            opmerking: Type.Optional(makeNullable(Type.String())),
                                            toetsNiveau: Type.Optional(makeNullable(Type.String())),
                                            normVersie: Type.Optional(makeNullable(Type.String())),
                                            normVersieAnders: Type.Optional(makeNullable(Type.String())),
                                            modelleerNiveau: Type.Optional(makeNullable(Type.String())),
                                            wegindeling: Type.Optional(makeNullable(Type.String())),
                                            aantalZwareVoertuigenPerJaar: Type.Optional(makeNullable(Type.String())),
                                            aantalZwareVoertuigenPerJaarAnders: Type.Optional(makeNullable(Type.String())),
                                            restLevensduur: Type.Optional(makeNullable(Type.String())),
                                            beperkingVoertuigGewicht: Type.Optional(makeNullable(Type.String())),
                                            beperkingAslast: Type.Optional(makeNullable(Type.String())),
                                            bestanden: Type.Optional(
                                                Type.Array(
                                                    Type.Object({
                                                        assetId: Type.String(),
                                                        description: Type.Optional(Type.String()),
                                                        filename: Type.String(),
                                                    }),
                                                ),
                                            ),
                                            score: Type.Optional(Type.Number()),
                                        }),
                                    ),
                                }),
                            ),
                        },
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/elements/verificatieBerekening/objectTree': {
        parameters: [createPathGuidParameter('surveyId')],
        get: {
            operationId: 'verificatieBerekeningenControllers.getElements',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Array(
                                Type.Object({
                                    units: Type.Array(
                                        Type.Object({
                                            icons: Type.Array(
                                                Type.Object({
                                                    iconName: Type.String(),
                                                    severity: Type.String(),
                                                }),
                                            ),
                                            inspectionStandardData: Type.Optional(
                                                Type.Object({
                                                    unityChecks: Type.Array(
                                                        Type.Object({
                                                            score: Type.Number(),
                                                            id: Type.String(),
                                                            unityCheckType: Type.String(),
                                                            unityCheckTypeAnders: Type.Optional(makeNullable(Type.String())),
                                                            modelleerNiveau: Type.String(),
                                                            unityCheckScore: Type.Number(),
                                                        }),
                                                    ),
                                                    score: Type.Optional(makeNullable(Type.Number())),
                                                    opmerking: Type.Optional(makeNullable(Type.String())),
                                                }),
                                            ),
                                            manifestations: Type.Array(
                                                Type.Intersect([
                                                    Type.Object({
                                                        id: Type.String(),
                                                    }),
                                                    Type.Object({
                                                        inspectionStandardData: Type.Object({}),
                                                    }),
                                                    Type.Object({
                                                        name: Type.String(),
                                                    }),
                                                    Type.Object({
                                                        code: Type.String(),
                                                    }),
                                                    Type.Object({
                                                        elementId: Type.String(),
                                                    }),
                                                    Type.Object({
                                                        unitId: Type.String(),
                                                    }),
                                                    Type.Object({}),
                                                ]),
                                            ),
                                            id: Type.String(),
                                            name: Type.String(),
                                            code: Type.String(),
                                            isArchived: Type.Boolean(),
                                            elementId: Type.String(),
                                            material: Type.String(),
                                        }),
                                    ),
                                    icons: Type.Array(
                                        Type.Object({
                                            iconName: Type.String(),
                                            severity: Type.String(),
                                        }),
                                    ),
                                    id: Type.String(),
                                    inspectionStandardData: Type.Object({}),
                                    name: Type.String(),
                                    code: Type.String(),
                                    elementGroupName: Type.String(),
                                    isArchived: Type.Boolean(),
                                }),
                            ),
                        },
                    },
                },
            },
        },
    },
    '/api/surveys/{surveyId}/elementGroups/verificatieBerekening/objectTree': {
        parameters: [createPathGuidParameter('surveyId')],
        get: {
            operationId: 'verificatieBerekeningenControllers.getElementGroups',
            responses: {
                '200': {
                    description: 'Result of the operation',
                    content: {
                        'application/json': {
                            schema: Type.Array(
                                Type.Object({
                                    elementGroupName: Type.Enum(ElementGroupNames),
                                    elements: Type.Array(
                                        Type.Object({
                                            units: Type.Array(
                                                Type.Object({
                                                    icons: Type.Array(
                                                        Type.Object({
                                                            iconName: Type.String(),
                                                            severity: Type.String(),
                                                        }),
                                                    ),
                                                    inspectionStandardData: Type.Optional(
                                                        Type.Object({
                                                            unityChecks: Type.Array(
                                                                Type.Object({
                                                                    score: Type.Number(),
                                                                    id: Type.String(),
                                                                    unityCheckType: Type.String(),
                                                                    unityCheckTypeAnders: Type.Optional(makeNullable(Type.String())),
                                                                    modelleerNiveau: Type.String(),
                                                                    unityCheckScore: Type.Number(),
                                                                }),
                                                            ),
                                                            score: Type.Optional(makeNullable(Type.Number())),
                                                            opmerking: Type.Optional(makeNullable(Type.String())),
                                                        }),
                                                    ),
                                                    manifestations: Type.Array(
                                                        Type.Intersect([
                                                            Type.Object({
                                                                id: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                inspectionStandardData: Type.Object({}),
                                                            }),
                                                            Type.Object({
                                                                name: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                code: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                elementId: Type.String(),
                                                            }),
                                                            Type.Object({
                                                                unitId: Type.String(),
                                                            }),
                                                            Type.Object({}),
                                                        ]),
                                                    ),
                                                    id: Type.String(),
                                                    name: Type.String(),
                                                    code: Type.String(),
                                                    isArchived: Type.Boolean(),
                                                    elementId: Type.String(),
                                                    material: Type.String(),
                                                }),
                                            ),
                                            icons: Type.Array(
                                                Type.Object({
                                                    iconName: Type.String(),
                                                    severity: Type.String(),
                                                }),
                                            ),
                                            id: Type.String(),
                                            inspectionStandardData: Type.Object({}),
                                            name: Type.String(),
                                            code: Type.String(),
                                            elementGroupName: Type.String(),
                                            isArchived: Type.Boolean(),
                                        }),
                                    ),
                                    icons: Type.Array(
                                        Type.Object({
                                            iconName: Type.String(),
                                            severity: Type.String(),
                                        }),
                                    ),
                                }),
                            ),
                        },
                    },
                },
            },
        },
    },
} as const;
